import type { TOC } from '@ember/component/template-only';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import {
  PrismComboSelectInput,
  PrismLoadingBar,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralAskErnest from 'lumen/components/general/ask-ernest';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralReportScheduleReportsModals from 'lumen/components/general/report-schedule/reports-modals';
import HotelMarketInsightCalendarsSidebarCalendar from 'lumen/components/hotel/market-insight/calendars/sidebar-calendar';
import HotelMarketInsightCityMap from 'lumen/components/hotel/market-insight/city-map';
import HotelMarketInsightDetailPane from 'lumen/components/hotel/market-insight/detail-pane';
import HotelMarketInsightModalsRatePreferenceModal from 'lumen/components/hotel/market-insight/modals/rate-preference-modal';
import HotelMarketInsightOverview from 'lumen/components/hotel/market-insight/overview';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelMarketInsightController from 'lumen/controllers/hotel/market-insight';
import can from 'lumen/helpers/can';
import hasFeature from 'lumen/helpers/has-feature';
import {
  ASK_ERNEST_PAGES,
  MARKET_PROMPTS,
} from 'lumen/utils/constants/ask-ernest-constants';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';

interface HotelMarketInsightSignature {
  Args: {
    controller: HotelMarketInsightController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  <GeneralPageHeader
    class="market-insight"
    role="tooltip"
    {{didInsert @controller.updateMapRender}}
    {{didInsert @controller.initMiSelections}}
    {{didUpdate @controller.validateSearchesTab @controller.isCityMapLoading}}
    {{didUpdate @controller.updateMapRender @controller.selectedDay}}
    {{on "mouseenter" (fn @controller.onNavbarHoverChange true)}}
    {{on "mouseleave" (fn @controller.onNavbarHoverChange false)}}
  >
    <div class="content-wrapper">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        {{! make sure we have a correct minAllowed & maxAllowedDay}}
        {{#if @controller.hasInitialisedMiSelections}}
          <div class="mr-200">
            <GeneralDatepickersDatepickerWithSidebar
              @displayEvents={{false}}
              @maxDate={{@controller.miSelections.maxAllowedDay}}
              @maxRange="1 year"
              @minDate={{@controller.miSelections.minAllowedDay}}
              @range={{@controller.datePickerRange}}
              @rangeChanged={{@controller.rangeChanged}}
              @sidebarLists={{@controller.datePickerRangeOptions}}
            />
          </div>
        {{/if}}
      </div>

      {{! Vs. X days ago reference selection }}
      <p class="pr-200 pl-200">
        {{t "market_insight.demand"}}{{#if @controller.access.hasOTB}}/{{t
            "rates.OTB"
          }}{{/if}}
      </p>
      <div
        class="flex flex-row items-center"
        data-test-id="mi-demand-ref-days-ago-dropdown"
      >
        <PrismSelectInput
          @onChange={{@controller.onRefDaysAgoSelectionChanged}}
          @options={{@controller.refDaysAgoSelectionOptions}}
          @value={{@controller.selectedRefDaysAgo}}
        />
      </div>

      <GeneralFilterBarFilterBar
        @activeFilters={{@controller.mappedActiveFilters}}
        @favourites={{@controller.favourites}}
        @filterBarSegmentationClicked={{@controller.clearActiveFilter}}
        @hideFavourites={{false}}
        @hotel={{@controller.hotelOrMarket}}
        @isHovered={{@controller.isNavbarHovered}}
        @isLoading={{@controller.model.calendar.isLoading}}
        @onClearFilterSelection={{@controller.onClearFilterSelection}}
        @pageType={{FILTER_BAR_PAGETYPES.MARKET_INSIGHT}}
        @segmentationFilters={{@controller.activeFilters}}
        @setFilterValues={{@controller.updateActiveFiltersToParameter}}
        class="ic-product-tour-market-insight-filter-bar"
      />

      <div class="flex items-center justify-center gap-200">
        <PrismComboSelectInput
          @horizontalPosition="right"
          @onChange={{@controller.onSettingsSelect}}
          @options={{@controller.miSettingsOptions}}
          @value={{@controller.miSettingsValue}}
        />

        {{#unless @controller.selectedDay}}
          <GeneralDropdownsDropdownExport
            @export={{@controller.handleExport}}
            @exportChoices={{@controller.exportChoices}}
          />
        {{/unless}}

        {{#if
          (and
            (hasFeature "app.agentic_interface")
            @controller.hasInitialisedMiSelections
          )
        }}
          <GeneralFilterBarDivider />

          <GeneralAskErnest
            @dateRange={{hash
              endDate=@controller.toDateMoment
              startDate=@controller.fromDateMoment
            }}
            @page={{ASK_ERNEST_PAGES.MARKET}}
            @prompts={{MARKET_PROMPTS}}
          />
        {{/if}}
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain {{didInsert @controller.onDidInsertMarketInsightElements}}>
    <div class="market-insight content-wrapper main-content clearfix">
      {{#if @controller.isLoadingGlobal}}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
      {{/if}}

      <div class="main-sidebar-and-content-container flex flex-row">
        <HotelMarketInsightCalendarsSidebarCalendar
          @hasReachedMainSidebarScrollThreshold={{@controller.hasReachedMainSidebarScrollThreshold}}
          @initPerfectScrollbarMainSidebar={{@controller.initPerfectScrollbarMainSidebar}}
          @isMarketInsightFull={{@controller.isMarketInsightFull}}
          @mainSidebarWrapperId={{@controller.mainSidebarWrapperId}}
          @model={{@controller.model}}
          @screenHeight={{@controller.screenHeight}}
          @selectedCalendarViewFilterIds={{@controller.selectedCalendarViewFilterIds}}
          @selectedDayMoment={{@controller.selectedDayMoment}}
          @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
          @selectedStartDate={{@controller.fromDateMoment}}
          @setSelectedCalendarViewFilterIds={{@controller.setSelectedCalendarViewFilterIds}}
          @sidebarCalendarRange={{@controller.sidebarCalendarRange}}
          @updateSelectedDay={{@controller.updateSelectedDay}}
        />

        <div class="flex flex-row" id="main-mi-content-container">
          {{#if @controller.selectedDayModel}}
            <HotelMarketInsightDetailPane
              @clearActiveFilter={{@controller.clearActiveFilter}}
              @currentExtractDate={{@controller.currentExtractDateUTC}}
              @focusedDetailSection={{@controller.focusedDetailSection}}
              @isMarketInsightFull={{@controller.isMarketInsightFull}}
              @model={{@controller.model}}
              @parsedActiveFilters={{@controller.parsedActiveFilters}}
              @rateIndexDataForCurrentSelection={{@controller.rateIndexDataForCurrentSelection}}
              @refDaysAgo={{@controller.refDaysAgo}}
              @selectedDay={{@controller.selectedDay}}
              @selectedDayModel={{@controller.selectedDayModel}}
              @setFocusedDetailSection={{@controller.setFocusedDetailSection}}
              @setHighlightedMarker={{@controller.setHighlightedMarker}}
              @updateSearchesDetailsItem={{@controller.updateSearchesDetailsItem}}
            />

            {{#if
              (and
                @controller.shouldRenderMap
                (not @controller.model.map.isLoading)
              )
            }}
              <HotelMarketInsightCityMap
                @currentExtractDate={{@controller.currentExtractDateUTC}}
                @extractDateIndex={{@controller.extractDateIndex}}
                @isDetailRequestLoading={{@controller.isDetailPaneLoading}}
                @isMarketInsightFull={{@controller.isMarketInsightFull}}
                @mapData={{@controller.model.map.data}}
                @model={{@controller.model}}
                @mouseOutMarkerId={{@controller.mouseOutMarkerId}}
                @mouseOverMarkerId={{@controller.mouseOverMarkerId}}
                @nbOfAvailableExtractDates={{@controller.nbOfAvailableExtractDates}}
                @rateIndexDataForCurrentSelection={{@controller.rateIndexDataForCurrentSelection}}
                @selectedDay={{@controller.selectedDay}}
                @selectedDayModel={{@controller.selectedDayModel}}
                @setFocusedDetailSection={{@controller.setFocusedDetailSection}}
                @shouldWaitToRenderEventMarkers={{@controller.shouldWaitToRenderEventMarkers}}
                @showAcceptedEvents={{@controller.showAcceptedEvents}}
                @showOwnHotel={{@controller.showOwnHotel}}
                @showPrimaryCompset={{@controller.showPrimaryCompset}}
                @showSmartCompset={{@controller.showSmartCompset}}
                @showSoldouts={{@controller.showSoldouts}}
                @toggleMapVisibilityFlag={{@controller.toggleMapVisibilityFlag}}
                @toggleShowAllEvents={{@controller.toggleShowAllEvents}}
                @updateExtractDateIndex={{@controller.updateExtractDateIndex}}
              />
            {{/if}}
          {{else}}
            <HotelMarketInsightOverview
              @isMarketInsightFull={{@controller.isMarketInsightFull}}
              @isOverviewLoading={{@controller.isOverviewLoading}}
              @isSearchesCardExpanded={{@controller.isSearchesCardExpanded}}
              @model={{@controller.model.overview}}
              @overviewRange={{@controller.overviewRange}}
              @refDaysAgo={{@controller.refDaysAgo}}
              @removeScrollTo={{@controller.removeScrollTo}}
              @scrollTo={{@controller.scrollTo}}
              @setActiveFilters={{@controller.setActiveFilters}}
              @setDateAggregation={{@controller.setDateAggregation}}
              @setFocusedDetailSection={{@controller.setFocusedDetailSection}}
              @toggleSearchesCard={{@controller.toggleSearchesCard}}
              @updateOTBTab={{@controller.updateOTBTab}}
              @updateSearchesDetailsItem={{@controller.updateSearchesDetailsItem}}
              @updateSearchesTab={{@controller.updateSearchesTab}}
            />
          {{/if}}
        </div>
      </div>

    </div>
  </GeneralPageMain>

  {{#if @controller.isShowingMIRatePreferenceModal}}
    <PrismModal
      @onClose={{@controller.closeRatePreferenceSettingsModal}}
      @title={{t "market_insight.settings.rate_preference.title"}}
    >
      <HotelMarketInsightModalsRatePreferenceModal
        @closeModal={{@controller.closeRatePreferenceSettingsModal}}
        @doRefresh={{@controller.doRefresh}}
      />
    </PrismModal>
  {{/if}}

  <GeneralReportScheduleReportsModals
    @canCreateNewUser={{can "hotel.reports.create.other.new"}}
    @canCreateOtherReports={{can "hotel.reports.create.other.existing"}}
    @isShowingScheduleModal={{@controller.scheduleReport}}
    @onCloseScheduleModal={{@controller.closeScheduleModal}}
    @type="market_insight"
  />
  {{outlet}}
</template> satisfies TOC<HotelMarketInsightSignature>;
